/**
 * Theme init. Maps API theme (organization.configurations.theme) to ThemeColors via the triplet pipeline.
 * Appearance (light/dark) is user-controlled: AsyncStorage + profile preferences, default light — not OS.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useThemeStore } from './store';
import type { ThemeColors } from './store';
import { getTheme, type ApiTheme } from '../../data/domains/config';
import { usePreferencesStore } from '../../data/domains/preferences';
import {
  buildFullThemeColors,
  DEFAULT_BRAND_TRIPLET,
  type BrandTriplet,
} from './colorUtils';

/** Local persistence for theme appearance (independent of OS color scheme). */
export const THEME_APPEARANCE_STORAGE_KEY = '@eataami_theme_appearance';

function hex(api: ApiTheme | null, key: keyof ApiTheme): string | undefined {
  const v = api?.[key];
  return typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v) ? v : undefined;
}

function resolveTriplet(api: ApiTheme | null): BrandTriplet {
  return {
    primary: hex(api, 'primary') ?? DEFAULT_BRAND_TRIPLET.primary,
    secondary: hex(api, 'secondary') ?? DEFAULT_BRAND_TRIPLET.secondary,
    tertiary: hex(api, 'tertiary') ?? DEFAULT_BRAND_TRIPLET.tertiary,
  };
}

/**
 * Macro colors: prefer explicit API keys, then legacy Material-style keys, else generated in buildFullThemeColors.
 */
function resolveMacroOverrides(api: ApiTheme | null): Partial<Pick<ThemeColors, 'calorie' | 'protein' | 'carbs' | 'fats'>> {
  const out: Partial<Pick<ThemeColors, 'calorie' | 'protein' | 'carbs' | 'fats'>> = {};
  const calorie =
    hex(api, 'calorie') ?? hex(api, 'on_primary_container');
  const protein =
    hex(api, 'protein') ?? hex(api, 'on_secondary_container');
  const carbs =
    hex(api, 'carbs') ?? hex(api, 'on_tertiary_container');
  const fats =
    hex(api, 'fats') ?? hex(api, 'on_error_container');
  if (calorie) out.calorie = calorie;
  if (protein) out.protein = protein;
  if (carbs) out.carbs = carbs;
  if (fats) out.fats = fats;
  return out;
}

export function resolveThemeFromApi(api: ApiTheme | null, scheme: 'light' | 'dark'): ThemeColors {
  const triplet = resolveTriplet(api);
  const macroOverrides = resolveMacroOverrides(api);
  const hasAnyMacro = Object.keys(macroOverrides).length > 0;
  return buildFullThemeColors(triplet, scheme, hasAnyMacro ? { macroOverrides } : undefined);
}

/**
 * Read saved appearance: local key wins if set; otherwise profile `preferences.theme` when it is
 * `light` | `dark` (`system` ignored → fall through); otherwise default `light`.
 */
export async function resolveInitialThemeScheme(): Promise<'light' | 'dark'> {
  try {
    const stored = await AsyncStorage.getItem(THEME_APPEARANCE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    /* use fallbacks below */
  }
  const t = usePreferencesStore.getState().preferences?.theme;
  if (t === 'light' || t === 'dark') {
    return t;
  }
  return 'light';
}

export async function initializeTheme(): Promise<void> {
  const apiTheme = getTheme();
  const scheme = await resolveInitialThemeScheme();
  try {
    await AsyncStorage.setItem(THEME_APPEARANCE_STORAGE_KEY, scheme);
  } catch {
    /* still apply theme */
  }
  useThemeStore.getState().setAppearance(scheme);
  const full = resolveThemeFromApi(apiTheme, scheme);
  useThemeStore.setState({ colors: full });
  useThemeStore.getState().setInitialized(true);
}

/**
 * Rebuild palette for the given scheme (same API triplet + macro overrides).
 */
export function refreshThemeForScheme(scheme: 'light' | 'dark'): void {
  const full = resolveThemeFromApi(getTheme(), scheme);
  useThemeStore.setState({ colors: full });
}

/**
 * Persist user choice, update in-memory preferences for profile sync, and apply colors.
 */
export async function persistThemeAppearance(scheme: 'light' | 'dark'): Promise<void> {
  await AsyncStorage.setItem(THEME_APPEARANCE_STORAGE_KEY, scheme);
  useThemeStore.getState().setAppearance(scheme);
  refreshThemeForScheme(scheme);
  const prefs = usePreferencesStore.getState().preferences;
  usePreferencesStore.getState().setPreferences({ ...prefs, theme: scheme });
}
